/**
 * „Nicht da" — eine Sperrzeit eintragen, ändern oder aufheben (Benjamin, 16.10.2026).
 *
 * Drei Dinge und sonst nichts: ganztägig (vorgewählt) oder von–bis, ein
 * Bis-Datum für mehrere Tage, eine freiwillige Notiz. Für wen: man selbst,
 * mit einem Umschalter für den anderen Inhaber. Eine neue Sperre trägt
 * „Eintragen" ein; eine bestehende speichert sich selbst wie der Termin.
 * Sperren aus einem Kalender (Abo mit „sperrt") stehen hier nur zum Lesen.
 *
 * Rahmen und Kopf sind die der Rückfrage bei Terminkonflikten (konflikt.css).
 *
 * Dasselbe Blatt trägt den **Bürotag** ein (20.10.2026, `art: "buero"`): vorgewählt
 * für beide Inhaber (zwei Zeilen mit gleichem Zeitraum). Ein Bürotag für beide
 * wird als einer geändert und aufgehoben.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fehlerText } from "@vimaana/ui";

import {
  bueroGeschwister, letzterTag, namenListe, sperrenProTag, useSperreAendern, useSperreAnlegen, useSperreAufheben,
  useSperrzeiten, vorname,
  type SperrArt, type Sperre, type SperreEingabe, type SperreGespeichert, type SperrenDaten,
} from "../../hooks/useSperrzeiten";

const BEIDE = "__beide";
import "../../styles/konflikt.css";
import "../../styles/sperrzeit.css";

/** Sperren für das Raster plus das Blatt — PlanungPage braucht nur diese vier Dinge. */
export function usePlanerSperren(from: string, to: string) {
  const q = useSperrzeiten(from, to);
  const proTag = useMemo(() => sperrenProTag(q.data?.sperren ?? []), [q.data]);
  const [offen, setOffen] = useState<{ tag: string; sperre?: Sperre; art?: SperrArt } | null>(null);
  const fuer = useCallback((tag: string) => proTag.get(tag) ?? [], [proTag]);
  const dialog = offen && q.data ? (
    <SperrBlatt daten={q.data} tag={offen.tag} sperre={offen.sperre} art={offen.art} onClose={() => setOffen(null)} />
  ) : null;
  return {
    fuer,
    neu: (tag: string, art: SperrArt = "abwesend") => setOffen({ tag, art }),
    oeffnen: (sperre: Sperre, tag: string) => setOffen({ tag, sperre }),
    dialog,
  };
}

const datumText = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "numeric" });

function SperrBlatt({ daten, tag, sperre, art: neueArt, onClose }: {
  daten: SperrenDaten; tag: string; sperre?: Sperre; art?: SperrArt; onClose: () => void;
}) {
  const anlegen = useSperreAnlegen();
  const aendern = useSperreAendern();
  const aufheben = useSperreAufheben();
  const nurLesen = sperre?.quelle === "kalender";
  const art: SperrArt = sperre?.art ?? neueArt ?? "abwesend";
  const buero = art === "buero";
  /* Ein bestehender Bürotag für beide: alle Zeilen desselben Zeitraums zusammen ändern. */
  const geschwister = useMemo(
    () => (sperre && buero ? bueroGeschwister(sperre, daten.sperren) : []), [sperre, buero, daten.sperren]);

  const [person, setPerson] = useState(
    sperre?.person_id || (buero && daten.inhaber.length > 1 ? BEIDE : daten.ich || daten.inhaber[0]?.id || ""));
  const [ganztags, setGanztags] = useState(sperre ? sperre.ganztags : true);
  const [vonTag, setVonTag] = useState(sperre ? sperre.von.slice(0, 10) : tag);
  const [bisTag, setBisTag] = useState(sperre ? (sperre.ganztags ? letzterTag(sperre) : sperre.bis.slice(0, 10)) : tag);
  const [vonZeit, setVonZeit] = useState(sperre && !sperre.ganztags ? sperre.von.slice(11, 16) : "08:00");
  const [bisZeit, setBisZeit] = useState(sperre && !sperre.ganztags ? sperre.bis.slice(11, 16) : "12:00");
  const [notiz, setNotiz] = useState(sperre?.notiz || "");
  const [ergebnis, setErgebnis] = useState<SperreGespeichert | null>(null);
  const [sicher, setSicher] = useState(false);

  const eingabe: SperreEingabe = {
    person_id: person && person !== BEIDE ? person : undefined,
    art,
    ganztags,
    von: ganztags ? vonTag : `${vonTag}T${vonZeit}`,
    bis: ganztags ? bisTag : `${bisTag}T${bisZeit}`,
    notiz,
  };
  const gueltig = ganztags ? bisTag >= vonTag : `${bisTag}T${bisZeit}` > `${vonTag}T${vonZeit}`;

  /* Bestehende Sperre: speichert sich 0,8 s nach der letzten Änderung selbst,
     beim Schließen sofort. */
  const stand = JSON.stringify(eingabe);
  const gesichert = useRef(stand);
  const sperreId = sperre?.id ?? null;
  const { mutate: aendernMutate } = aendern;
  const sichern = useCallback((jetzt: string) => {
    if (sperreId === null || nurLesen || jetzt === gesichert.current) return;
    gesichert.current = jetzt;
    const werte = JSON.parse(jetzt) as SperreEingabe;
    if (geschwister.length > 1) {
      /* Bürotag für beide: jede Zeile behält ihre Person, Zeit und Notiz ziehen mit. */
      for (const g of geschwister) {
        if (g.id !== null) aendernMutate({ id: g.id, ...werte, person_id: g.person_id }, { onSuccess: setErgebnis });
      }
      return;
    }
    aendernMutate({ id: sperreId, ...werte }, { onSuccess: setErgebnis });
  }, [sperreId, nurLesen, aendernMutate, geschwister]);
  useEffect(() => {
    if (!gueltig) return;
    const t = window.setTimeout(() => sichern(stand), 800);
    return () => window.clearTimeout(t);
  }, [stand, gueltig, sichern]);
  const schliessen = () => { if (gueltig) sichern(stand); onClose(); };

  const eintragen = async () => {
    const fuer = person === BEIDE ? daten.inhaber.map((p) => p.id) : [eingabe.person_id];
    try {
      const antworten: SperreGespeichert[] = [];
      for (const id of fuer) antworten.push(await anlegen.mutateAsync({ ...eingabe, person_id: id }));
      const eingeplant = antworten.flatMap((r) => r.eingeplant);
      if (eingeplant.length) setErgebnis({ sperre: antworten[0].sperre, eingeplant });
      else onClose();
    } catch { /* Fehler zeigt anlegen.error */ }
  };

  const vonAendern = (v: string) => { setVonTag(v); if (bisTag < v) setBisTag(v); };
  const fehler = anlegen.error || aendern.error || aufheben.error;
  const ichName = (id: string) => (id === daten.ich ? "Ich" : vorname(daten.inhaber.find((p) => p.id === id)?.name || ""));
  const wer = ergebnis ? vorname(ergebnis.sperre.person_name) : "";

  return (
    <div className="kf-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) schliessen(); }}>
      <section className="kf-blatt sz-blatt" role="dialog" aria-modal="true" aria-labelledby="sz-titel">
        <header className="kf-kopf">
          <h3 id="sz-titel">
            {nurLesen ? `${vorname(sperre?.person_name || "")} nicht da`
              : buero ? (sperre ? "Bürotag — ändern" : "Bürotag")
              : sperre ? "Nicht da — ändern" : "Nicht da"}
          </h3>
          <button type="button" className="kf-zu" aria-label="Schließen" onClick={schliessen}>×</button>
        </header>

        {nurLesen && sperre ? (
          <div className="kf-inhalt">
            <p className="kf-frage">
              {datumText(sperre.von)}{letzterTag(sperre) !== sperre.von.slice(0, 10) ? ` bis ${datumText(letzterTag(sperre))}` : ""}
              {sperre.ganztags ? " · ganztägig" : ` · ${sperre.von.slice(11, 16)}–${sperre.bis.slice(11, 16)}`}
              {sperre.notiz ? ` · ${sperre.notiz}` : ""}
            </p>
            <p className="sz-leise">Kommt aus dem Kalender „{sperre.kalender}“ — ändern lässt es sich nur dort.</p>
          </div>
        ) : (
          <div className="kf-inhalt">
            {/* Nach dem Eintragen ist die Sperre gespeichert — dann nur noch der Hinweis. */}
            <fieldset className="sz-felder" disabled={!sperre && !!ergebnis}>
            {geschwister.length > 1 ? (
              <p className="sz-leise">Für {namenListe(geschwister)}</p>
            ) : daten.inhaber.length > 1 && (
              <div className="sz-wer" role="radiogroup" aria-label="Für wen">
                {[...daten.inhaber].sort((a, b) => Number(b.id === daten.ich) - Number(a.id === daten.ich)).map((p) => (
                  <button key={p.id} type="button" role="radio" aria-checked={person === p.id}
                    className={"sz-wer-knopf" + (person === p.id ? " ist-an" : "")} onClick={() => setPerson(p.id)}>
                    {ichName(p.id)}
                  </button>
                ))}
                {buero && !sperre && (
                  <button type="button" role="radio" aria-checked={person === BEIDE}
                    className={"sz-wer-knopf" + (person === BEIDE ? " ist-an" : "")} onClick={() => setPerson(BEIDE)}>
                    Beide
                  </button>
                )}
              </div>
            )}

            <label className="sz-schalter">
              <input type="checkbox" checked={ganztags} onChange={() => setGanztags(!ganztags)} />
              <span>Ganztägig</span>
            </label>

            <div className="sz-reihe">
              <span className="sz-marke">Von</span>
              <input type="date" className="sz-feld" value={vonTag} onChange={(e) => vonAendern(e.target.value)} />
              {!ganztags && <input type="time" className="sz-feld sz-zeit" value={vonZeit} step={900} onChange={(e) => setVonZeit(e.target.value)} />}
            </div>
            <div className="sz-reihe">
              <span className="sz-marke">Bis</span>
              <input type="date" className="sz-feld" value={bisTag} min={vonTag} onChange={(e) => setBisTag(e.target.value)} />
              {!ganztags && <input type="time" className="sz-feld sz-zeit" value={bisZeit} step={900} onChange={(e) => setBisZeit(e.target.value)} />}
            </div>
            {!gueltig && <p className="sz-fehler">Das Ende liegt vor dem Beginn.</p>}

            <input className="sz-feld sz-notiz" value={notiz} maxLength={200} onChange={(e) => setNotiz(e.target.value)}
              placeholder={buero ? "Worum geht’s? (freiwillig)" : "Notiz (freiwillig)"} />
            </fieldset>

            {ergebnis && ergebnis.eingeplant.length > 0 && (
              <div className="kf-hinweis" role="status">
                {person === BEIDE ? "In der Zeit schon eingeplant" : ergebnis.sperre.person_id === daten.ich ? "Du bist in der Zeit schon eingeplant" : `${wer} ist in der Zeit schon eingeplant`}:
                <ul className="sz-liste">
                  {ergebnis.eingeplant.map((e) => (
                    <li key={`${e.termin_id}-${e.von}`}>{e.bezeichnung} · {datumText(e.von)} {e.von.slice(11, 16)}–{e.bis.slice(11, 16)}</li>
                  ))}
                </ul>
                Beim nächsten Speichern dieser Termine fragt Kontor nach.
              </div>
            )}
            {fehler && <p className="sz-fehler">{fehlerText(fehler)}</p>}
          </div>
        )}

        <footer className="kf-fuss">
          {sperre?.id && !nurLesen && (sicher ? (
            <button type="button" className="vg-btn sz-rot" disabled={aufheben.isPending}
              onClick={async () => {
                const ids = geschwister.length > 1 ? geschwister.map((g) => g.id as number) : [sperre.id as number];
                try { for (const id of ids) await aufheben.mutateAsync(id); onClose(); } catch { /* aufheben.error */ }
              }}>
              {aufheben.isPending ? "…" : "Wirklich aufheben"}
            </button>
          ) : (
            <button type="button" className="vg-btn vg-btn-ghost sz-rot" onClick={() => setSicher(true)}>Aufheben</button>
          ))}
          <span className="sz-status" aria-live="polite">
            {sperre?.id && !nurLesen ? (aendern.isPending ? "Speichert …" : aendern.isSuccess ? "✓ Gespeichert" : "") : ""}
          </span>
          {!sperre && !ergebnis && (
            <button type="button" className="vg-btn vg-btn-primary" disabled={!gueltig || !person || anlegen.isPending} onClick={eintragen}>
              {anlegen.isPending ? "Trägt ein …" : "Eintragen"}
            </button>
          )}
          {(sperre || ergebnis) && (
            <button type="button" className="vg-btn vg-btn-primary" onClick={schliessen}>Fertig</button>
          )}
        </footer>
      </section>
    </div>
  );
}
